/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {
    FC,
    $Sprite,
    ExtendContainerProps,
    $Container,
    getProp,
    patchEvents,
    $SetVisibility,
    responsiveSkipChildren,
} from '@gongxigames/framework/core/fragments/BuilderY';
import {Store} from '@gongxigames/framework/core/fragments/store/Store';
import {IHitArea} from 'pixi.js';

export const BUTTON_STATES = ['disabled', 'focused', 'pressed', 'normal'];

export type ButtonProps = ExtendContainerProps<{
    name: string;
    disabled?: boolean;
    highlighted?: boolean;
    action?: () => void;

    /**
     * `Pixi.Rectangle` or `Pixi.Circle`
     * if `null` - automatic hit area will be calculated
     * please use predefined hit area not `null`
     */
    hitArea: IHitArea|null;
}>;

export const $Button: FC<ButtonProps> = (props) => {
    const focused = Store.signal(false);
    const pressed = Store.signal(false);
    const enabled = Store.computed(() => !getProp(props.disabled));
    const highlighted = Store.computed(() => getProp(props.highlighted));

    const setHoverTrue = () => focused.value = true;
    const setHoverFalse = () => focused.value = false;
    const setPressTrue = () => pressed.value = true;
    const setPressFalse = () => pressed.value = false;
    const runCallBack = () => getProp(props.action)?.();

    props.events ??= {};
    patchEvents(props.events, {
        pointerover: setHoverTrue,
        pointerout: setHoverFalse,
        pointerdown: setPressTrue,
        pointerup: setPressFalse,
        pointertap: runCallBack,
        pointerupoutside: setPressFalse,
    });

    const selected = Store.computed(() => {
        if (getProp(props.disabled)) {
            return 'disabled';
        }

        if (pressed.value) {
            return 'pressed';
        }

        if (focused.value || highlighted.value) {
            return 'focused';
        }

        return 'normal';
    });

    const stateComponents = Store.computed(() => {
        const name = getProp(props.name);

        return BUTTON_STATES.map(state =>{
            const texture = `${name}_${state}_sys_ui`;

            return <$Sprite
                name={state}
                anchor={0.5}
                texture={texture}
            />;
        });
    });

    responsiveSkipChildren(props);

    return <$Container
        interactive={enabled}
        buttonMode={enabled}
        {...props}
    >
        <$SetVisibility selector={selected}>
            {stateComponents}
        </$SetVisibility>
    </$Container>;
};